import React, { useEffect, useRef, useState } from "react";
import { useCertificateDownload, useStyles, useTranslations } from "examsbook-react-sdk/custom-hooks";
import lock from "../../assets/images/lock.png";
import defaultCertificateImage from "../../assets/images/certificateTemplate.png";
import { useDispatch, useSelector } from "react-redux";
import { courseCertificateListAction } from "examsbook-react-sdk/actions/certificateAddResponse";
import { Typography, Box, Button, useMediaQuery } from "@material-ui/core";
import { useTheme as useMuiTheme } from "@material-ui/core/styles";
import { setUnlockCertificate } from "examsbook-react-sdk/actions/getSelfLearningAction";
import html2canvas from "html2canvas";
import axios from "axios";
import {
  WhatsappShareButton, TwitterShareButton, FacebookShareButton,
  WhatsappIcon, TwitterIcon, FacebookIcon
} from "react-share";

export default function CertificateCard({ sectionData }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const t = useTranslations();
  const userName = user?.user?.name ? user?.user?.name : user?.name;
  const classes = useStyles(styles)();
  const [certificateData, setCertificateData] = useState([]);
  const { handleDownloadCertificate } = useCertificateDownload({ userName });
  const [isLoading, setIsLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [certificateUrl, setCertificateUrl] = useState(null);
  const certificateRef = useRef(null);
  const settingsData = useSelector((state) => state.settingsReducers.settingsData?.data);
  const certificateBg = settingsData?.data?.[0]?.branding?.certificateImage?.imagePath
    ? settingsData.data[0].branding.certificateImage.imagePath
    : defaultCertificateImage;

  useEffect(() => {
    if (sectionData?.data?.courseId?._id) {
      dispatch(courseCertificateListAction(sectionData?.data?.courseId?._id)).then((res) => {
        setIsLoading(true);
        if (res?.data?.length) {
          setCertificateData(res?.data[0]);
          dispatch(setUnlockCertificate(res?.data?.[0]?.isCompleted));
        }
      });
    }
  }, [sectionData?.data?.courseId?._id]);

  const isCertCompleted = certificateData && certificateData?.isCompleted === true;
  const courseName = useSelector((state) => state.filteredCourseList.courseName);
  const shareUrl = window.location.href;
  const shareTitle = `🎉 I just completed "${certificateData?.courseId?.name || courseName}" and earned my certificate!`;

  const uploadCertificate = async () => {
    if (certificateUrl) return certificateUrl;
    const canvas = await html2canvas(certificateRef.current, { useCORS: true, scale: 2 });
    return new Promise((resolve, reject) => {
      canvas.toBlob(async (blob) => {
        try {
          const formData = new FormData();
          formData.append("certificate", blob, "certificate.png");
          const token = localStorage.getItem("token") || sessionStorage.getItem("token");
          const res = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/v1/uploadCertificate`,
            formData,
            { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
          );
          const url = res?.data?.url;
          setCertificateUrl(url);
          resolve(url);
        } catch (err) { reject(err); }
      }, "image/png");
    });
  };

  const handleShare = async (platform) => {
    if (!certificateRef.current || sharing) return;
    try {
      setSharing(true);
      const shareWindow = window.open("", "_blank");
      const url = await uploadCertificate();

      // Build the OG share page URL
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const completedDate = certificateData?.completedOn
        ? new Date(certificateData.completedOn).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : "";
      const sharePageUrl = `${backendUrl}/certificate/share?name=${encodeURIComponent(userName)}&course=${encodeURIComponent(certificateData?.courseId?.name || courseName)}&date=${encodeURIComponent(completedDate)}&image=${encodeURIComponent(url)}`;

      const text = encodeURIComponent(shareTitle);
      const encodedPageUrl = encodeURIComponent(sharePageUrl);
      const links = {
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedPageUrl}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedPageUrl}`,
        twitter: `https://twitter.com/intent/tweet?url=${encodedPageUrl}&text=${text}`,
        whatsapp: `https://api.whatsapp.com/send?text=${text}%20${sharePageUrl}`,
      };
      if (shareWindow) shareWindow.location.href = links[platform];
    } catch (err) {
      console.error("Share failed", err);
    } finally {
      setSharing(false);
    }
  };

  return (
    <>
      <Box style={{ minHeight: "20px" }}>
        {!isCertCompleted && isLoading ? (
          <Typography className={classes.certifiedText}>
            *{t("Complete the course and pass the evaluation test with")}{" "}
            {sectionData?.data?.evaluationTest[0]?.testId?.passingPercent}%{" "}
            {t("to unlock the certificate")}
          </Typography>
        ) : (
          isLoading && (
            <Typography className={classes.certifiedTextPass}>
              ✅ {t("Well done! You've successfully unlocked your course certificate, and it's ready to download.")}
            </Typography>
          )
        )}
      </Box>

      {/* Certificate Preview */}
      {isCertCompleted && (
        <Box ref={certificateRef} style={{ margin: "0 auto 12px", position: "relative", display: "block", width: "70%" }}>
          <img src={certificateBg} alt="Certificate Preview" style={{ width: "100%", borderRadius: "4px", display: "block" }} />
          {/* Name overlay - matches AlexBrush 42px at top:156px on 446px height = ~35% */}
          <Box style={{ position: "absolute", top: "35%", width: "100%", textAlign: "center" }}>
            <Typography style={{ fontFamily: "'AlexBrush', cursive", fontSize: "clamp(60px, 6vw, 44px)", color: "#333", letterSpacing: "0px" }}>
              {userName}
            </Typography>
            {/* Content text - matches Literata 12px, marginTop:40px after name */}
            <Typography style={{ fontFamily: "serif", fontSize: "clamp(16px, 2.8vw, 24px)", color: "#333", marginTop: "6px" }}>
              You have successfully completed <strong>{certificateData?.courseId?.name || courseName}</strong> Course from
            </Typography>
            <Typography style={{ fontFamily: "serif", fontSize: "clamp(16px, 3vw, 26px)", fontWeight: "600", color: "#333" }}>
              <strong>LEARNIFY</strong> on {new Date(certificateData?.completedOn).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })} 
            </Typography>
          </Box>
        </Box>
      )}

      {/* Download & Share Buttons */}
      {isCertCompleted ? (
        <Box style={{ display: "flex", alignItems: "center", margin: "0 16px 16px", position: "relative" }}>
          <Box style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleDownloadCertificate(certificateData?.courseId?.name, certificateData?.completedOn)}
            >
              {t("Download")}
            </Button>
          </Box>
          <Box style={{ display: "flex", alignItems: "center", gap: "10px", marginLeft: "auto" }}>
            {/* Share certificate image via Cloudinary */}
            <Box onClick={() => handleShare("linkedin")} style={{ cursor: sharing ? "not-allowed" : "pointer", borderRadius: "50%", overflow: "hidden", width: 32, height: 32, opacity: sharing ? 0.6 : 1 }}>
              <svg viewBox="0 0 64 64" width="32" height="32"><circle cx="32" cy="32" r="32" fill="#0077b5"/><path d="M20.4 42.6V26.2h-5.4v16.4h5.4zm-2.7-18.6c1.9 0 3-1.2 3-2.8-.1-1.6-1.2-2.8-3-2.8s-3 1.2-3 2.8c0 1.6 1.1 2.8 2.9 2.8h.1zm5.7 18.6h5.4V33.4c0-.5 0-1 .2-1.3.4-1 1.4-2 3-2 2.1 0 3 1.6 3 4v8.5h5.4V33.4c0-5.3-2.8-7.7-6.6-7.7-3.1 0-4.4 1.7-5.2 2.9v.1h-.1l.1-.1v-2.4h-5.4c.1 1.5 0 16.4 0 16.4z" fill="#fff"/></svg>
            </Box>
            <Box onClick={() => handleShare("whatsapp")} style={{ cursor: sharing ? "not-allowed" : "pointer", borderRadius: "50%", overflow: "hidden", width: 32, height: 32, opacity: sharing ? 0.6 : 1 }}>
              <WhatsappIcon size={32} round />
            </Box>
            <Box onClick={() => handleShare("twitter")} style={{ cursor: sharing ? "not-allowed" : "pointer", borderRadius: "50%", overflow: "hidden", width: 32, height: 32, opacity: sharing ? 0.6 : 1 }}>
              <TwitterIcon size={32} round />
            </Box>
            <Box onClick={() => handleShare("facebook")} style={{ cursor: sharing ? "not-allowed" : "pointer", borderRadius: "50%", overflow: "hidden", width: 32, height: 32, opacity: sharing ? 0.6 : 1 }}>
              <FacebookIcon size={32} round />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box style={{ display: "flex", alignItems: "center", gap: "8px", margin: "0 16px 16px" }}>
          <img src={lock} alt="Locked" style={{ maxWidth: "15px" }} />
          <Typography style={{ fontSize: "12px", color: "#999" }}>
            {certificateData?.courseId?.name || courseName || sectionData?.data?.courseId?.name}
          </Typography>
        </Box>
      )}
    </>
  );
}

const styles = {
  certifiedText: {
    color: "#F75151",
    fontSize: "12px",
    fontWeight: 500,
    fontStyle: "italic",
    margin: "12px 16px",
  },
  certifiedTextPass: {
    color: "#2e7d32",
    fontSize: "12px",
    fontWeight: 500,
    fontStyle: "italic",
    margin: "12px 16px",
  },
};
